/**
 * compileErrorBus
 *
 * Eén centrale verzamelplaats voor compile- (Vite HMR) en runtime-fouten.
 * - Luistert naar Vite's `vite:error` event in dev (compile / transform errors).
 * - Vangt window 'error' en 'unhandledrejection' (runtime crashes).
 * - Parseert de boodschap om de ontbrekende identifier (bv. "WhatsBrokenBanner")
 *   en bestand:regel:kolom uit te lichten.
 *
 * Gebruik:
 *   import { compileErrorBus } from "@/lib/compileErrorBus";
 *   compileErrorBus.subscribe(setErrors);
 */

export type CapturedErrorKind = "compile" | "runtime";

export interface CapturedError {
  id: string;
  kind: CapturedErrorKind;
  message: string;
  /** Highlighted missing/undefined identifier, if we could extract one. */
  missingSymbol?: string;
  file?: string;
  line?: number;
  column?: number;
  /** Original frame for context (compile errors mostly). */
  frame?: string;
  receivedAt: number;
}

type Listener = (errors: CapturedError[]) => void;

const MAX_ERRORS = 8;

// Patterns that typically reveal a missing/undefined identifier
const SYMBOL_PATTERNS: RegExp[] = [
  /Cannot find name ['"`]([A-Za-z_$][\w$]*)['"`]/,
  /['"`]([A-Za-z_$][\w$]*)['"`] is not defined/,
  /ReferenceError:\s+([A-Za-z_$][\w$]*)\s+is not defined/,
  /Identifier ['"`]([A-Za-z_$][\w$]*)['"`] has already been declared/,
  /Module ['"`]([^'"`]+)['"`] has no exported member ['"`]([A-Za-z_$][\w$]*)['"`]/,
  /export ['"`]([A-Za-z_$][\w$]*)['"`] .* not found/,
];

const FILE_LOC_PATTERNS: RegExp[] = [
  // src/foo/bar.tsx:12:5
  /([\/\w.\-@]+\.(?:tsx?|jsx?|mjs|cjs|css)):(\d+):(\d+)/,
  // (src/foo/bar.tsx:12:5)
  /\(([\/\w.\-@]+\.(?:tsx?|jsx?|mjs|cjs|css)):(\d+):(\d+)\)/,
];

const extractSymbol = (msg: string): string | undefined => {
  for (const re of SYMBOL_PATTERNS) {
    const m = msg.match(re);
    if (m) return m[m.length - 1];
  }
  return undefined;
};

const extractFileLoc = (
  msg: string,
  fallbackFile?: string,
): { file?: string; line?: number; column?: number } => {
  for (const re of FILE_LOC_PATTERNS) {
    const m = msg.match(re);
    if (m) {
      return { file: m[1], line: Number(m[2]), column: Number(m[3]) };
    }
  }
  return { file: fallbackFile };
};

class CompileErrorBus {
  private errors: CapturedError[] = [];
  private listeners = new Set<Listener>();
  private installed = false;

  install() {
    if (this.installed || typeof window === "undefined") return;
    this.installed = true;

    // Vite HMR compile/transform errors
    if (import.meta.hot) {
      import.meta.hot.on("vite:error", (payload: any) => {
        const err = payload?.err ?? payload;
        const message: string = err?.message || err?.stack || String(err);
        const loc = err?.loc as
          | { file?: string; line?: number; column?: number }
          | undefined;
        const fromMsg = extractFileLoc(message, loc?.file);
        this.push({
          id: crypto.randomUUID(),
          kind: "compile",
          message,
          missingSymbol: extractSymbol(message),
          file: loc?.file ?? fromMsg.file,
          line: loc?.line ?? fromMsg.line,
          column: loc?.column ?? fromMsg.column,
          frame: err?.frame,
          receivedAt: Date.now(),
        });
      });
      // When a successful update flushes the overlay, clear our state too
      import.meta.hot.on("vite:afterUpdate", () => {
        if (this.errors.some((e) => e.kind === "compile")) {
          this.errors = this.errors.filter((e) => e.kind !== "compile");
          this.emit();
        }
      });
    }

    // Runtime errors
    window.addEventListener("error", (event) => {
      // Ignore ResizeObserver noise
      if (/ResizeObserver loop/i.test(event.message)) return;
      const loc = extractFileLoc(
        event.error?.stack || event.message,
        event.filename,
      );
      this.push({
        id: crypto.randomUUID(),
        kind: "runtime",
        message: event.message || String(event.error),
        missingSymbol: extractSymbol(
          event.message + " " + (event.error?.stack ?? ""),
        ),
        file: loc.file ?? event.filename,
        line: loc.line ?? event.lineno,
        column: loc.column ?? event.colno,
        receivedAt: Date.now(),
      });
    });

    window.addEventListener("unhandledrejection", (event) => {
      const reason: any = event.reason;
      const message: string =
        reason?.message || (typeof reason === "string" ? reason : String(reason));
      const stack: string = reason?.stack || "";
      const loc = extractFileLoc(stack);
      this.push({
        id: crypto.randomUUID(),
        kind: "runtime",
        message: `Unhandled promise rejection: ${message}`,
        missingSymbol: extractSymbol(message + " " + stack),
        file: loc.file,
        line: loc.line,
        column: loc.column,
        receivedAt: Date.now(),
      });
    });
  }

  /** Manually report an error (used by ErrorBoundary). */
  report(input: Omit<CapturedError, "id" | "receivedAt">) {
    this.push({
      ...input,
      id: crypto.randomUUID(),
      receivedAt: Date.now(),
      missingSymbol: input.missingSymbol ?? extractSymbol(input.message),
    });
  }

  private push(err: CapturedError) {
    // De-dupe consecutive identical messages
    const last = this.errors[0];
    if (last && last.message === err.message && last.file === err.file) return;
    this.errors = [err, ...this.errors].slice(0, MAX_ERRORS);
    this.emit();
  }

  clear() {
    if (this.errors.length === 0) return;
    this.errors = [];
    this.emit();
  }

  dismiss(id: string) {
    this.errors = this.errors.filter((e) => e.id !== id);
    this.emit();
  }

  get current(): CapturedError[] {
    return this.errors;
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.errors);
    return () => this.listeners.delete(fn);
  }

  private emit() {
    for (const fn of this.listeners) fn(this.errors);
  }
}

export const compileErrorBus = new CompileErrorBus();
